// noinspection DuplicatedCode

import {format} from 'date-fns';
import HumanReadableTime from '../models/HumanReadableTime';
import {Runner} from '../models/runner';
import RunProvider, {LANGUAGE_DE} from './run';
import RenderProvider from './render';

export default class RunnerStatistics {
    static instance = null;

    private secondsInterval: ReturnType<typeof setInterval>;

    private runProvider: RunProvider;

    private renderProvider: RenderProvider;

    private runner: Runner;

    distance: number;

    time: HumanReadableTime;

    pace: HumanReadableTime;

    private readonly spinnerElement: string;

    static getInstance(): RunnerStatistics {
        if (!this.instance) {
            this.instance = new RunnerStatistics();
        }

        return this.instance;
    }

    constructor() {
        this.runProvider = RunProvider.getInstance();
        this.renderProvider = RenderProvider.getInstance();

        if (this.renderProvider.statisticsSpinnerTemplate) {
            this.spinnerElement = this.renderProvider.statisticsSpinnerTemplate.innerHTML;
        }
    }

    subscribe(runner: Runner) {
        if (this.secondsInterval) {
            this.unsubscribe();
        }

        this.runner = runner;

        this.updateSeconds();

        this.secondsInterval = setInterval(() => this.updateSeconds(), 1000);

        this.renderProvider.statisticsEtaSwitch.addEventListener('click', RunnerStatistics.handleEtaSwitch);
    }

    unsubscribe() {
        if (this.renderProvider.runnerStatistics) {
            this.renderProvider.runnerStatistics.classList.add('hidden');
        }

        this.runner = null;

        clearInterval(this.secondsInterval);

        this.renderProvider.statisticsEtaSwitch.removeEventListener('click', RunnerStatistics.handleEtaSwitch);
    }

    static handleEtaSwitch(event: Event) {
        event.preventDefault();

        const etaSwitch = RunnerStatistics.getEtaSwitch();
        etaSwitch.dataset.status = etaSwitch.dataset.status === 'timeOfDay' ? 'default' : 'timeOfDay';
    }

    updateSeconds() {
        if (!this.runner || !this.runner.hasStarted() || this.runProvider.hasRunEnded(this.runner)) {
            this.unsubscribe();
            return;
        }

        this.renderProvider.runnerStatistics.classList.remove('hidden');

        this.distance = this.runner.getDistanceInterpolated();
        this.pace = this.runner.getPaceBasedOnResultsInKm();
        this.time = this.runner.getTime();

        if (!this.distance || !this.pace || !this.time) {
            this.renderAllSpinners();
            return;
        }

        this.renderProvider.runnerStatistics.classList.remove('hidden');

        this.renderDistance();

        const time = RunnerStatistics.displayTime(this.time);
        if (this.time.time > 0) {
            RunnerStatistics.updateOnlyIfChanged(this.renderProvider.statisticsTime, time || '');
        } else {
            this.renderSpinner(this.renderProvider.statisticsTime);
        }

        this.renderPace();

        this.renderEta();
    }

    renderAllSpinners() {
        this.renderSpinner(this.renderProvider.statisticsDistance);
        this.renderSpinner(this.renderProvider.statisticsTime);
        this.renderSpinner(this.renderProvider.statisticsSpeed);
        this.renderSpinner(this.renderProvider.statisticsEta);
    }

    renderSpinner(element: Element) {
        RunnerStatistics.updateOnlyIfChanged(element, this.spinnerElement);
    }

    renderDistance() {
        let distanceString: string = this.distance ? this.distance.toFixed(1)
            .toString() : '';
        if (distanceString.indexOf('.0') !== -1) {
            // eslint-disable-next-line prefer-destructuring
            distanceString = distanceString.split('.')[0];
        }
        distanceString = distanceString.replace('.', ',');

        if (distanceString.length) {
            const renderedDistance = this.renderProvider.statisticsDistanceTemplate.innerHTML
                .replace('%distance%', distanceString);
            RunnerStatistics.updateOnlyIfChanged(this.renderProvider.statisticsDistance, renderedDistance);
        } else {
            this.renderSpinner(this.renderProvider.statisticsDistance);
        }
    }

    static getEtaSwitch(): HTMLButtonElement {
        return document.querySelector('.js-runner-statistics-eta-switch') as HTMLButtonElement;
    }

    static showTimeOfDayEta(): boolean {
        return RunnerStatistics.getEtaSwitch().dataset.status === 'timeOfDay';
    }

    renderEta() {
        const showTimeOfDayEta = RunnerStatistics.showTimeOfDayEta();

        let eta: string = RunnerStatistics.displayTime(this.runner.eta);
        if (this.runner.eta.time <= 0) {
            this.renderSpinner(this.renderProvider.statisticsEta);
            return;
        }

        if (showTimeOfDayEta) {
            eta = this.displayTimeOfDay(this.runner.getEtaTimeOfDay());
        }

        const label = this.runProvider.translate(showTimeOfDayEta ? '~ Uhrzeit' : '~ Zielzeit');
        const renderedEta = this.renderProvider.statisticsEtaTemplate.innerHTML
            .replace('%value%', eta || '–')
            .replace('%label%', label);
        RunnerStatistics.updateOnlyIfChanged(this.renderProvider.statisticsEta, renderedEta);
    }

    renderPace() {
        const pace = RunnerStatistics.displayTime(this.pace, false);
        if (!this.runner.hasResults() || this.pace.time <= 0) {
            this.renderSpinner(this.renderProvider.statisticsSpeed);
            this.renderProvider.statisticsPaceIndicator.classList.add('hidden');
            return;
        }

        const renderedPace = this.renderProvider.statisticsSpeedTemplate.innerHTML
            .replace('%pace%', pace || '–');
        RunnerStatistics.updateOnlyIfChanged(this.renderProvider.statisticsSpeed, renderedPace);

        const paceIsGood = this.runner.isFastEnoughForPersonalEta();
        const goodClass = 'runner-statistics__corner-icon--green';
        const badClass = 'runner-statistics__corner-icon--yellow';

        this.renderProvider.statisticsPaceIndicator.classList.remove('hidden', paceIsGood ? badClass : goodClass);
        this.renderProvider.statisticsPaceIndicator.classList.add(paceIsGood ? goodClass : badClass);
    }

    static updateOnlyIfChanged(element: Element, content: string) {
        if (element.innerHTML !== content) {
            // eslint-disable-next-line no-param-reassign
            element.innerHTML = content;
        }
    }

    static leadingZero(value: number): string {
        return value < 10 ? `0${value}` : value.toString();
    }

    static makeTwoDigitString(value: number): string {
        if (value === undefined || Number.isNaN(value)) {
            return '';
        }

        if (Math.floor(value) < 10 && value >= 0) {
            return `0${value.toString()}`;
        }

        return value.toString();
    }

    static displayTime(
        value: number | HumanReadableTime,
        displayHours = true,
        displayMinutes = true,
        displaySeconds = true,
        displayOperator = false,
    ): string {
        const time = value instanceof HumanReadableTime ? value : new HumanReadableTime(value);

        if (!time || time.time === undefined) {
            return '';
        }

        const valueStrings: string[] = [];

        if (displayHours && !Number.isNaN(time.hours)) {
            valueStrings.push(RunnerStatistics.makeTwoDigitString(time.hours));
        }

        if (displayMinutes && !Number.isNaN(time.minutes)) {
            valueStrings.push(RunnerStatistics.makeTwoDigitString(time.minutes));
        }

        if (displaySeconds && !Number.isNaN(time.seconds)) {
            valueStrings.push(RunnerStatistics.makeTwoDigitString(time.seconds));
        }

        // eslint-disable-next-line no-nested-ternary
        const operator = (!displayOperator || time.time === 0) ? '' : (time.time > 0 ? '+' : '-');

        return `${operator}${valueStrings.join(':')}`;
    }

    displayTimeOfDay(value: Date): string {
        if (this.runProvider.getLanguage() !== LANGUAGE_DE) {
            return format(value, 'HH:mm');
        }

        return `${format(value, 'HH:mm')} Uhr`;
    }
}
